import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { Subject, takeUntil, switchMap } from 'rxjs';
import { ContentApiService } from '../../core/services/content-api.service';
import { SectionRendererComponent } from '../../shared/components/section-renderer/section-renderer.component';

@Component({
  selector: 'app-dynamic-page',
  standalone: true,
  imports: [CommonModule, SectionRendererComponent, RouterLink],
  template: `
    @if (loading) {
      <div class="mx-auto max-w-7xl px-6 lg:px-8 py-24">
        <div class="skeleton h-12 w-80 max-w-full mb-5"></div>
        <div class="skeleton h-5 w-full max-w-xl mb-3"></div>
        <div class="skeleton h-5 w-full max-w-lg mb-12"></div>
        <div class="skeleton h-72 rounded-2xl mb-8"></div>
        <div class="skeleton h-5 w-full max-w-md"></div>
      </div>
    }

    @if (page) {
      @for (section of page.sections; track section._id || $index) {
        <app-section-renderer [section]="section" />
      }
    }

    @if (notFound) {
      <div class="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div class="font-heading text-[8rem] font-black text-surface-100 leading-none mb-2 tracking-[-0.04em]">404</div>
        <h1 class="font-heading text-2xl font-bold text-surface-900 mb-3 tracking-[-0.02em]">Page Not Found</h1>
        <p class="text-surface-500 mb-10 max-w-md leading-relaxed">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          routerLink="/"
          class="group inline-flex items-center gap-2.5 px-7 py-3 bg-surface-900 text-white font-semibold text-sm rounded-xl hover:bg-surface-800 transition-all duration-200"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to Home
        </a>
      </div>
    }
  `,
})
export class DynamicPageComponent implements OnInit, OnDestroy {
  page: any = null;
  loading = true;
  notFound = false;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private contentApi: ContentApiService,
    private title: Title,
    private meta: Meta,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(
      takeUntil(this.destroy$),
      switchMap(params => {
        this.loading = true;
        this.notFound = false;
        this.page = null;
        return this.contentApi.getPageBySlug(params['slug']);
      })
    ).subscribe({
      next: page => {
        this.page = page;
        this.loading = false;
        this.cdr.detectChanges();
        if (page?.seo?.title || page?.title) {
          this.title.setTitle(page.seo?.title || page.title);
        }
        if (page?.seo?.description) {
          this.meta.updateTag({ name: 'description', content: page.seo.description });
        }
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
